'use client'

import { ReactNode, useEffect, useState } from 'react'
import { Tooltip, offset, useFloating } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { getLocalStorageItem, setLocalStorageItem } from '@marketplace-web/shared/browser'
import { useLatestCallback } from '@marketplace-web/shared/ui-state'

const createLocalStorageCounter = (key: string) => {
  return {
    increment() {
      const current = this.get()
      setLocalStorageItem(key, String(current + 1))
    },
    get() {
      return Number(getLocalStorageItem(key) || '0') || 0
    },
  }
}

const interactionCount = createLocalStorageCounter('search_by_image_interacted_count')

const TOOLTIP_DURATION_MS = 6e3
const MAX_INTERACTION_COUNT = 3

type Props = {
  children: ReactNode
}

const SearchByImageCta = ({ children }: Props) => {
  const translate = useTranslate('searchbar.search_by_image.modal')
  const [isOpen, setIsOpen] = useState(false)
  const badgeFloating = useFloating({
    placement: 'top-end',
    shouldAutoUpdate: true,
    middleware: [
      offset({
        mainAxis: -7,
        crossAxis: 0,
      }),
    ],
    strategy: 'fixed',
  })

  const [isBadgeShown, setIsBadgeShown] = useState(false)

  const refreshBadgeIsShown = useLatestCallback(() => {
    if (interactionCount.get() >= MAX_INTERACTION_COUNT) {
      setIsBadgeShown(false)

      return
    }

    if (interactionCount.get() > 0) {
      setIsBadgeShown(true)

      return
    }

    setIsBadgeShown(!isOpen)
  })

  useEffect(refreshBadgeIsShown, [isOpen, refreshBadgeIsShown])

  useEffect(() => {
    setIsOpen(interactionCount.get() === 0)
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsOpen(false)
    }, TOOLTIP_DURATION_MS)

    return () => {
      clearTimeout(timeout)
    }
  }, [])

  const handleClick = () => {
    interactionCount.increment()
    refreshBadgeIsShown()
    setIsOpen(false)
  }

  return (
    <Tooltip
      content={translate('tooltip')}
      show={isOpen}
      hover={false}
      shouldAutoUpdate
      placement="bottom"
    >
      <div role="none" onClick={handleClick} ref={badgeFloating.triggerRef}>
        {children}

        {isBadgeShown && (
          <div
            className="search-by-image-new-badge"
            style={badgeFloating.floaterStyle}
            ref={badgeFloating.floaterRef}
          >
            {translate('new')}
          </div>
        )}
      </div>
    </Tooltip>
  )
}

export default SearchByImageCta
